/**
 * TablePotView for Poker Reborn
 *
 * Displays the main pot and side pots in the center of the table.
 * Pure display component - all game logic is on the server.
 *
 * @see docs/screens/room/TABLE_POT.md - Table pot specification
 * @see docs/screens/room/CHIPS_DISPLAY.md - Animated chip display
 * @see docs/screens/room/ROOM_HIERARCHY.md - Layer structure
 */

import { Container, Graphics, Text } from 'pixi.js';
import { Chips } from '../chips';
import { ChipsDisplay } from '@/components/common/ChipsDisplay';
import { addOriginMarker, ORIGIN_COLORS } from '@/utils/OriginDebug';

// =============================================================================
// TYPES
// =============================================================================

export interface SidePot {
  id: number;
  amount: number;
  eligiblePlayers?: string[];
}

export interface WinnerInfo {
  seatNumber: number;
  amount: number;
  position?: { x: number; y: number };
}

// =============================================================================
// STYLES
// =============================================================================

const STYLES = {
  /** Background */
  background: {
    fill: 0x1a1a2e,
    alpha: 0.25,
    radius: 10,
    padding: { x: 12, y: 6 },
  },

  /** Main pot */
  mainPot: {
    label: {
      text: 'POT',
      fill: 0xaaaaaa, // Grey
      fontSize: 10,
      fontFamily: 'Roboto, Arial, sans-serif',
      y: 26, // Below amount
    },
    amount: {
      color: 0xffd700, // Gold
      increaseColor: 0x4caf50, // Green when pot grows
      decreaseColor: 0xffd700, // Gold (pot doesn't decrease during hand)
      fontSize: 20,
      y: 8, // Above label, with gap from chips
    },
    chips: {
      size: 'medium' as const,
      maxChips: 8,
    },
  },

  /** Side pot */
  sidePot: {
    label: {
      fill: 0xaaaaaa, // Grey (same as main pot)
      fontSize: 10,
      fontFamily: 'Roboto, Arial, sans-serif',
      y: 24, // Below amount
    },
    amount: {
      color: 0xffaa00, // Orange
      increaseColor: 0x4caf50,
      decreaseColor: 0xffaa00,
      fontSize: 16,
      y: 8, // Above label, with gap from chips
    },
    chips: {
      size: 'small' as const,
      maxChips: 5,
    },
  },

  /** Pot spacing */
  sidePotSpacing: 80,

  /** Glow effect */
  glow: {
    size: 20,
    defaultColor: 0xffd700,
    defaultIntensity: 0.8,
  },
};

// =============================================================================
// TABLE POT DISPLAY - Reusable pot display unit
// =============================================================================

/**
 * TablePotDisplay - Display unit for a single pot (main or side)
 *
 * Contains glow, chips, label, and amount display.
 * Effects can target the `chips` component directly for animations.
 */
export class TablePotDisplay extends Container {
  readonly glow: Graphics;
  readonly chips: Chips;
  readonly labelText: Text;
  readonly amount: ChipsDisplay;

  private glowSize = STYLES.glow.size;

  constructor(options: {
    labelText: string;
    labelStyle: { fill: number; fontSize: number; fontFamily: string; y: number };
    amountStyle: {
      color: number;
      increaseColor: number;
      decreaseColor: number;
      fontSize: number;
      y: number;
    };
    chipsSize: 'small' | 'medium' | 'large';
    chipsMaxChips: number;
  }) {
    super();

    // Create glow (behind everything)
    this.glow = new Graphics();
    this.glow.label = 'glow';
    this.glow.visible = false;
    this.addChild(this.glow);

    // Create chips
    this.chips = new Chips({ size: options.chipsSize, maxChips: options.chipsMaxChips });
    this.chips.label = 'chips';
    this.chips.y = -25;
    this.addChild(this.chips);

    // Create amount display (above label)
    this.amount = new ChipsDisplay({
      color: options.amountStyle.color,
      increaseColor: options.amountStyle.increaseColor,
      decreaseColor: options.amountStyle.decreaseColor,
      fontSize: options.amountStyle.fontSize,
      prefix: '$',
      anchorX: 0.5,
      anchorY: 0.5,
    });
    this.amount.y = options.amountStyle.y;
    this.addChild(this.amount);

    // Create label (below amount)
    this.labelText = new Text({
      text: options.labelText,
      style: options.labelStyle,
    });
    this.labelText.anchor.set(0.5);
    this.labelText.y = options.labelStyle.y;
    this.addChild(this.labelText);
  }

  /**
   * Set the pot value
   */
  setValue(value: number, animated = true): void {
    this.chips.setValue(value, animated);
    this.amount.setValue(value, animated);
  }

  /**
   * Get current value
   */
  getValue(): number {
    return this.amount.getValue();
  }

  /**
   * Set glow effect
   */
  setGlow(color: number, intensity: number): void {
    this.glow.clear();

    if (intensity > 0) {
      // Draw glow behind the pot area
      this.glow.roundRect(
        -50 - this.glowSize,
        -35 - this.glowSize,
        100 + this.glowSize * 2,
        80 + this.glowSize * 2,
        STYLES.background.radius + this.glowSize / 2
      );
      this.glow.fill({ color, alpha: intensity * 0.4 });
      this.glow.visible = true;
    } else {
      this.glow.visible = false;
    }
  }

  /**
   * Clear glow effect
   */
  clearGlow(): void {
    this.setGlow(0, 0);
  }

  /**
   * Update label text (for side pots: "Side 1", "Side 2", etc.)
   */
  setLabel(text: string): void {
    this.labelText.text = text;
  }

  /**
   * Resize for different screen widths (BBS).
   * @param screenWidth - Current screen width in pixels
   */
  resize(screenWidth: number): void {
    this.chips.resize(screenWidth);
  }
}

// =============================================================================
// TABLE POT VIEW
// =============================================================================

export class TablePotView extends Container {
  private background: Graphics;
  private mainPotDisplay: TablePotDisplay;
  private sidePotDisplays: TablePotDisplay[] = [];

  private mainPotValue = 0;
  private sidePotsData: SidePot[] = [];

  /** When true, visibility is controlled by animation, not by state */
  private _animationControlled = false;

  constructor() {
    super();

    // Create background
    this.background = new Graphics();
    this.addChild(this.background);

    // Create main pot display
    this.mainPotDisplay = new TablePotDisplay({
      labelText: STYLES.mainPot.label.text,
      labelStyle: STYLES.mainPot.label,
      amountStyle: STYLES.mainPot.amount,
      chipsSize: STYLES.mainPot.chips.size,
      chipsMaxChips: STYLES.mainPot.chips.maxChips,
    });
    this.addChild(this.mainPotDisplay);

    // Initial layout
    this.layout();

    // Debug: Add origin marker if ?showOrigins=true
    addOriginMarker(this, ORIGIN_COLORS.tablePot);
  }

  // ===========================================================================
  // PUBLIC API
  // ===========================================================================

  /**
   * Set main pot amount
   */
  setAmount(amount: number): void {
    console.log('[TablePotView] setAmount:', amount, 'was:', this.mainPotValue);
    const previousPot = this.mainPotValue;
    this.mainPotValue = amount;

    // When pot is cleared (new hand), release animation control
    if (amount === 0) {
      console.log('[TablePotView] Clearing animation control (amount=0)');
      this._animationControlled = false;
    }

    this.updateDisplay(previousPot);
  }

  /**
   * Set all pots (main + side pots)
   */
  setPots(pots: Array<{ amount: number; id?: number; eligiblePlayers?: string[] }>): void {
    console.log(
      '[TablePotView] setPots:',
      pots.length,
      'pots, amounts:',
      pots.map((p) => p.amount)
    );
    const previousPot = this.mainPotValue;

    if (pots.length === 0) {
      console.log('[TablePotView] Clearing pots, releasing animation control');
      this.mainPotValue = 0;
      this.sidePotsData = [];
      this._animationControlled = false;
    } else {
      this.mainPotValue = pots[0]?.amount ?? 0;
      this.sidePotsData = pots.slice(1).map((p, index) => {
        const sidePot: SidePot = {
          id: p.id ?? index + 1,
          amount: p.amount,
        };
        if (p.eligiblePlayers) {
          sidePot.eligiblePlayers = p.eligiblePlayers;
        }
        return sidePot;
      });
    }

    this.updateDisplay(previousPot);
  }

  /**
   * Get total pot (main + all side pots)
   */
  getTotal(): number {
    return this.mainPotValue + this.sidePotsData.reduce((sum, p) => sum + p.amount, 0);
  }

  /**
   * Get main pot amount value
   */
  getMainPotValue(): number {
    return this.mainPotValue;
  }

  /**
   * Clear pot display
   */
  clear(): void {
    this.mainPotValue = 0;
    this.sidePotsData = [];
    this.mainPotDisplay.setValue(0, false);
    this.updateDisplay(0);
  }

  /**
   * Resize for different screen widths (BBS).
   * @param screenWidth - Current screen width in pixels
   */
  resize(screenWidth: number): void {
    this.mainPotDisplay.resize(screenWidth);
    for (const sidePotDisplay of this.sidePotDisplays) {
      sidePotDisplay.resize(screenWidth);
    }
  }

  // ===========================================================================
  // ANIMATION VISIBILITY CONTROL
  // ===========================================================================

  /**
   * Hide the pot for animation purposes.
   * While hidden for animation, state updates won't auto-show it.
   */
  hideForAnimation(): void {
    console.log('[TablePotView] hideForAnimation called, mainPot:', this.mainPotValue);
    this._animationControlled = true;
    this.visible = false;
  }

  /**
   * Show the pot after animation completes.
   * Releases animation control back to state.
   */
  showAfterAnimation(): void {
    console.log('[TablePotView] showAfterAnimation called, mainPot:', this.mainPotValue);
    this._animationControlled = false;
    this.updateVisibility();
  }

  /**
   * Check if pot is currently hidden for animation.
   */
  isHiddenForAnimation(): boolean {
    return this._animationControlled;
  }

  /**
   * Hide a specific pot for animation (by index).
   * Unlike hideForAnimation(), this only hides ONE pot display,
   * leaving other side pots visible.
   *
   * @param potIndex - 0 = main pot, 1+ = side pots
   */
  hidePotForAnimation(potIndex: number): void {
    console.log('[TablePotView] hidePotForAnimation:', potIndex);
    const potDisplay = this.getPot(potIndex);
    if (potDisplay) {
      potDisplay.visible = false;
      this.updateBackgroundVisibility();
    }
  }

  /**
   * Show a specific pot after animation (by index).
   *
   * @param potIndex - 0 = main pot, 1+ = side pots
   */
  showPotAfterAnimation(potIndex: number): void {
    console.log('[TablePotView] showPotAfterAnimation:', potIndex);
    const potDisplay = this.getPot(potIndex);
    if (potDisplay) {
      potDisplay.visible = true;
      this.updateBackgroundVisibility();
    }
  }

  /**
   * Check if a specific pot is hidden for animation.
   *
   * @param potIndex - 0 = main pot, 1+ = side pots
   */
  isPotHiddenForAnimation(potIndex: number): boolean {
    const potDisplay = this.getPot(potIndex);
    return potDisplay ? !potDisplay.visible : true;
  }

  /**
   * Get the global position of a specific pot's chips.
   * Used by chip effects to fly from the correct pot position.
   *
   * @param potIndex - 0 = main pot, 1+ = side pots
   * @returns Global position of the pot's chip stack, or undefined if not found
   */
  getPotChipPosition(potIndex: number): { x: number; y: number } | undefined {
    const potDisplay = this.getPot(potIndex);
    if (!potDisplay) {
      return undefined;
    }

    // Get the chips component position within the pot display
    // tablePotView.x + potDisplay.x + chips.x (chips are at y=-25 in display)
    const chipsX = this.x + potDisplay.x + potDisplay.chips.x;
    const chipsY = this.y + potDisplay.chips.y;

    return { x: chipsX, y: chipsY };
  }

  /**
   * Highlight a pot (glow effect).
   *
   * @param potIndex - Index of pot to highlight (0 = main pot, 1+ = side pots)
   */
  highlightPot(potIndex: number): void {
    const pot = this.getPot(potIndex);
    if (pot) {
      pot.setGlow(STYLES.glow.defaultColor, STYLES.glow.defaultIntensity);
    }
  }

  /**
   * Clear all pot highlights.
   */
  clearHighlight(): void {
    this.mainPotDisplay.clearGlow();
    for (const sidePot of this.sidePotDisplays) {
      sidePot.clearGlow();
    }
  }

  // ===========================================================================
  // PUBLIC ACCESSORS
  // ===========================================================================

  /**
   * Get main pot display
   */
  getMainPot(): TablePotDisplay {
    return this.mainPotDisplay;
  }

  /**
   * Get all side pot displays
   */
  getSidePots(): TablePotDisplay[] {
    return this.sidePotDisplays;
  }

  /**
   * Get pot by index (0 = main, 1+ = side pots)
   */
  getPot(index: number): TablePotDisplay | undefined {
    if (index === 0) {
      return this.mainPotDisplay;
    }
    return this.sidePotDisplays[index - 1];
  }

  /**
   * Get pot position for effect targeting
   */
  getPosition(): { x: number; y: number } {
    return { x: this.x, y: this.y };
  }

  // ===========================================================================
  // DEPRECATED - Keep for backwards compatibility during transition
  // ===========================================================================

  /** @deprecated Use getMainPot().chips instead */
  getMainPotChips(): Chips {
    return this.mainPotDisplay.chips;
  }

  /** @deprecated Use getMainPot().amount instead */
  getAmountDisplay(): ChipsDisplay {
    return this.mainPotDisplay.amount;
  }

  /** @deprecated Use getMainPot().glow instead */
  getPotGlow(): Graphics {
    return this.mainPotDisplay.glow;
  }

  /** @deprecated Use getMainPot().chips for targeting */
  getParticleAnchor(): Container {
    return this.mainPotDisplay.chips;
  }

  /** @deprecated Use highlightPot(0) instead */
  setPotGlow(color: number, intensity: number): void {
    this.mainPotDisplay.setGlow(color, intensity);
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  private updateDisplay(previousPot: number = 0): void {
    // Update main pot display
    if (this.mainPotValue > 0) {
      const shouldAnimate = previousPot > 0 && this.mainPotValue > previousPot;
      this.mainPotDisplay.setValue(this.mainPotValue, shouldAnimate);
    } else {
      this.mainPotDisplay.setValue(0, false);
    }

    // Reset individual pot display visibility (may have been hidden by hidePotForAnimation)
    this.mainPotDisplay.visible = true;
    for (const sidePot of this.sidePotDisplays) {
      sidePot.visible = true;
    }

    // Update visibility
    this.updateVisibility();

    // Update side pots
    this.updateSidePots();

    // Redraw background
    this.layout();
  }

  private updateVisibility(): void {
    const shouldBeVisible = this.mainPotValue > 0 || this.sidePotsData.length > 0;
    if (!this._animationControlled) {
      this.visible = shouldBeVisible;
    }
    this.updateBackgroundVisibility();
    console.log('[TablePotView] updateVisibility:', {
      mainPot: this.mainPotValue,
      animationControlled: this._animationControlled,
      shouldBeVisible,
      actuallyVisible: this.visible,
    });
  }

  /**
   * Update background visibility based on whether any pot displays are visible.
   * Background is hidden when all pot displays are hidden (e.g., during win distribution).
   */
  private updateBackgroundVisibility(): void {
    // Check if main pot is visible
    const mainPotVisible = this.mainPotDisplay.visible;

    // Check if any side pot is visible
    const anySidePotVisible = this.sidePotDisplays.some((pot) => pot.visible);

    // Background is visible only if at least one pot display is visible
    const shouldShowBackground = mainPotVisible || anySidePotVisible;
    this.background.visible = shouldShowBackground;
  }

  private updateSidePots(): void {
    // Remove excess side pot displays
    while (this.sidePotDisplays.length > this.sidePotsData.length) {
      const removed = this.sidePotDisplays.pop();
      if (removed) {
        this.removeChild(removed);
        removed.destroy();
      }
    }

    // Update or create side pot displays
    this.sidePotsData.forEach((potData, index) => {
      let display = this.sidePotDisplays[index];

      // Create new display if needed
      if (!display) {
        display = new TablePotDisplay({
          labelText: `Side ${String(potData.id)}`,
          labelStyle: STYLES.sidePot.label,
          amountStyle: STYLES.sidePot.amount,
          chipsSize: STYLES.sidePot.chips.size,
          chipsMaxChips: STYLES.sidePot.chips.maxChips,
        });
        this.sidePotDisplays.push(display);
        this.addChild(display);
      }

      // Update display
      display.setLabel(`Side ${String(potData.id)}`);
      display.setValue(potData.amount, false);

      // Position
      display.x = (index + 1) * STYLES.sidePotSpacing;
    });
  }

  private layout(): void {
    // Calculate total width
    const sidePotsWidth = this.sidePotsData.length * STYLES.sidePotSpacing;
    const mainPotWidth = 100;
    const totalWidth = mainPotWidth + sidePotsWidth;

    // Hide main pot label when there's only one pot (no side pots)
    const hasSidePots = this.sidePotsData.length > 0;
    this.mainPotDisplay.labelText.visible = hasSidePots;

    // Center the main pot
    const mainPotCenterX = -sidePotsWidth / 2;
    this.mainPotDisplay.x = mainPotCenterX;

    // Position side pots relative to main pot
    this.sidePotDisplays.forEach((display, index) => {
      display.x = mainPotCenterX + (index + 1) * STYLES.sidePotSpacing;
    });

    // Draw background
    const padding = STYLES.background.padding;
    const width = totalWidth + padding.x * 2;
    const height = 70 + padding.y * 2;

    this.background.clear();
    this.background.roundRect(-width / 2, -35, width, height, STYLES.background.radius);
    this.background.fill({ color: STYLES.background.fill, alpha: STYLES.background.alpha });
  }
}
